/**
 * Service Mapper
 * Maps WordPress service data to component Service interface
 */

import { Server, Lock, Network, Cloud, Database, Code, Shield, Gauge } from 'lucide-react';
import React from 'react';
import { getMainCategoryByWordPressCategory, getSubCategoryByWordPressCategory } from './serviceCategories';

export interface WordPressService {
  id: number;
  title: {
    rendered: string;
  };
  content: {
    rendered: string;
  };
  excerpt: {
    rendered: string;
  };
  slug: string;
  service_category?: number[]; // WordPress category IDs
  acf?: {
    name_ar?: string;
    icon?: string;
    color?: string;
    features?: string[];
    benefits?: string[];
  };
  _embedded?: {
    'wp:featuredmedia'?: Array<{
      source_url: string;
      alt_text: string;
    }>;
    'wp:term'?: Array<Array<{
      id: number;
      name: string;
      slug: string;
      taxonomy: string;
    }>>;
  };
}

export interface Service {
  id: number;
  name: string;
  nameAr: string;
  description: string;
  longDescription: string;
  icon: React.ReactNode;
  color: string;
  features: string[];
  benefits: string[];
  slug: string;
  imageUrl?: string;
  mainCategoryId?: string; // Main category ID (cybersecurity, platform-development, etc.)
  subCategoryId?: string; // Sub-category ID (offensive-security, defensive-security, etc.)
  wordPressCategoryName?: string; // Original WordPress category name
}

/**
 * Get icon component for service based on name or slug
 */
function getIconForService(name: string, slug: string): React.ReactNode {
  const lowerName = name.toLowerCase();
  const lowerSlug = slug.toLowerCase();

  if (lowerName.includes('strategy') || lowerName.includes('consulting') || lowerSlug.includes('strategy')) {
    return <Gauge className="w-8 h-8" />;
  }
  if (lowerName.includes('microservice') || lowerName.includes('micro-service') || lowerSlug.includes('microservice')) {
    return <Network className="w-8 h-8" />;
  }
  if (lowerName.includes('security') || lowerName.includes('cyber') || lowerSlug.includes('security') || lowerSlug.includes('cyber')) {
    return <Lock className="w-8 h-8" />;
  }
  if (lowerName.includes('cloud') || lowerSlug.includes('cloud')) {
    return <Cloud className="w-8 h-8" />;
  }
  if (lowerName.includes('api') || lowerSlug.includes('api')) {
    return <Code className="w-8 h-8" />;
  }
  if (lowerName.includes('devops') || lowerName.includes('dev-ops') || lowerSlug.includes('devops')) {
    return <Server className="w-8 h-8" />;
  }
  if (lowerName.includes('audit') || lowerName.includes('auditing') || lowerSlug.includes('audit')) {
    return <Shield className="w-8 h-8" />;
  }
  if (lowerName.includes('governance') || lowerSlug.includes('governance')) {
    return <Database className="w-8 h-8" />;
  }

  // Default icon
  return <Server className="w-8 h-8" />;
}

/**
 * Get color for service based on category or use default
 */
function getColorForService(name: string, slug: string, acfColor?: string): string {
  if (acfColor) {
    return acfColor;
  }

  const lowerName = name.toLowerCase();
  const lowerSlug = slug.toLowerCase();

  // Bareeq Tech brand colors
  const colors = {
    primary: '#007A3D', // Green
    secondary: '#D7182A', // Red
    accent: '#005BFF', // Blue
    gold: '#D4AF37',
    orange: '#E67E22',
    teal: '#1ABC9C',
    purple: '#9B59B6',
  };

  if (lowerName.includes('strategy') || lowerName.includes('consulting') || lowerSlug.includes('strategy')) {
    return colors.primary;
  }
  if (lowerName.includes('microservice') || lowerName.includes('micro-service') || lowerSlug.includes('microservice')) {
    return colors.secondary;
  }
  if (lowerName.includes('security') || lowerName.includes('cyber') || lowerSlug.includes('security') || lowerSlug.includes('cyber')) {
    return colors.gold;
  }
  if (lowerName.includes('cloud') || lowerSlug.includes('cloud')) {
    return colors.accent;
  }
  if (lowerName.includes('api') || lowerSlug.includes('api')) {
    return colors.purple;
  }
  if (lowerName.includes('devops') || lowerName.includes('dev-ops') || lowerSlug.includes('devops')) {
    return colors.teal;
  }
  if (lowerName.includes('audit') || lowerName.includes('auditing') || lowerSlug.includes('audit')) {
    return colors.orange;
  }
  if (lowerName.includes('governance') || lowerSlug.includes('governance')) {
    return colors.primary;
  }

  // Default color
  return colors.primary;
}

/**
 * Strip WPBakery/Visual Composer shortcodes from text
 */
function stripShortcodes(text: string): string {
  // Remove all shortcode patterns: [shortcode] or [shortcode attr="value"]
  return text.replace(/\[vc_[^\]]+\]/g, '').replace(/\[[^\]]+\]/g, '').trim();
}

/**
 * Decode HTML entities
 */
function decodeHtmlEntities(text: string): string {
  const textarea = document.createElement('textarea');
  textarea.innerHTML = text;
  return textarea.value;
}

/**
 * Strip HTML tags and shortcodes from text, and decode entities
 */
function stripHtml(html: string): string {
  if (!html) return '';
  
  // First decode HTML entities
  let cleaned = html;
  try {
    // Handle common HTML entities
    cleaned = cleaned
      .replace(/&#8221;/g, '"')
      .replace(/&#8220;/g, '"')
      .replace(/&#8217;/g, "'")
      .replace(/&#8216;/g, "'")
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'");
  } catch (e) {
    // If document is not available (SSR), skip entity decoding
  }
  
  // Remove WPBakery shortcodes
  cleaned = stripShortcodes(cleaned);
  
  // Remove HTML tags
  cleaned = cleaned.replace(/<[^>]*>/g, '');
  
  // Clean up extra whitespace
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
}

/**
 * Parse features/benefits from ACF or extract from content
 */
function parseListItems(text: string): string[] {
  // Try to extract list items from HTML
  const listMatch = text.match(/<ul[^>]*>([\s\S]*?)<\/ul>/i);
  if (listMatch) {
    const items = listMatch[1].match(/<li[^>]*>([\s\S]*?)<\/li>/gi);
    if (items) {
      return items.map(item => stripHtml(item)).filter(item => item.length > 0);
    }
  }

  // Try to extract from plain text with bullets
  const bulletItems = text.match(/[•\-\*]\s*([^\n]+)/g);
  if (bulletItems) {
    return bulletItems.map(item => item.replace(/[•\-\*]\s*/, '').trim()).filter(item => item.length > 0);
  }

  return [];
}

/**
 * Extract clean text from WPBakery content and create concise summary
 * Similar to Mobile Application Security Testing format
 */
function extractTextFromContent(content: string): string {
  if (!content) return '';
  
  // Decode HTML entities first
  let text = content
    .replace(/&#8221;/g, '"')
    .replace(/&#8220;/g, '"')
    .replace(/&#8217;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'");
  
  // Extract text from vc_column_text content (most common pattern)
  const columnTextPattern = /\[vc_column_text[^\]]*\]([\s\S]*?)\[\/vc_column_text\]/gi;
  const columnTextMatches = [...text.matchAll(columnTextPattern)];
  
  let extractedTexts: string[] = [];
  
  if (columnTextMatches && columnTextMatches.length > 0) {
    // Extract text from all vc_column_text blocks
    extractedTexts = columnTextMatches.map(match => {
      let innerText = match[1] || '';
      
      // Remove any nested shortcodes
      innerText = innerText.replace(/\[vc_[^\]]+\]/gi, '');
      innerText = innerText.replace(/\[\/[^\]]+\]/g, '');
      innerText = innerText.replace(/\[[^\]]+\]/g, '');
      
      // Remove HTML tags but preserve text
      innerText = innerText.replace(/<[^>]*>/g, ' ');
      
      // Clean up
      innerText = innerText.replace(/\s+/g, ' ').trim();
      
      return innerText;
    }).filter(t => t.length > 10);
  }
  
  // If no vc_column_text found, extract from HTML tags
  if (extractedTexts.length === 0) {
    // Remove all WPBakery shortcodes first
    text = text.replace(/\[vc_[^\]]+\]/gi, '');
    text = text.replace(/\[\/[^\]]+\]/g, '');
    text = text.replace(/\[[^\]]+\]/g, '');
    
    // Extract text from HTML tags (like <p>, <h1>, <h2>, etc.)
    text = text.replace(/<[^>]*>/g, ' ');
    
    // Extract meaningful sentences (at least 20 characters)
    const sentences = text.split(/[.!?]\s+/).filter(s => s.trim().length > 20);
    if (sentences.length > 0) {
      extractedTexts = sentences;
    }
  }
  
  // Create concise summary (like Mobile Application Security Testing format)
  if (extractedTexts.length > 0) {
    // Find the first substantial paragraph that describes what the service does
    // Look for sentences that contain action words and describe the service
    const serviceDescription = extractedTexts.find(t => {
      const lower = t.toLowerCase();
      return (lower.includes('provides') || lower.includes('delivers') || 
              lower.includes('evaluates') || lower.includes('assesses') ||
              lower.includes('designs') || lower.includes('builds') ||
              lower.includes('offers') || lower.includes('enables')) &&
             t.length > 30 && t.length < 300;
    });
    
    if (serviceDescription) {
      // Clean up and return the first good description
      return serviceDescription.trim();
    }
    
    // If no perfect match, use the first substantial text
    const firstSubstantial = extractedTexts.find(t => t.length > 50 && t.length < 250);
    if (firstSubstantial) {
      return firstSubstantial.trim();
    }
    
    // Fallback: join first few sentences
    return extractedTexts.slice(0, 2).join(' ').trim();
  }
  
  // Final cleanup
  text = text.replace(/\s+/g, ' ').trim();
  
  return text;
}

/**
 * Map WordPress service to component Service interface
 */
export function mapWordPressServiceToService(wpService: WordPressService): Service {
  const name = stripHtml(wpService.title.rendered);
  const slug = wpService.slug;
  
  // Extract clean text from content (removing WPBakery shortcodes)
  const rawContent = wpService.content.rendered || '';
  const content = extractTextFromContent(rawContent);
  
  // Use excerpt or extract from content
  const rawExcerpt = wpService.excerpt.rendered || '';
  const excerpt = stripHtml(rawExcerpt);

  // Get Arabic name from ACF or use English name
  const nameAr = wpService.acf?.name_ar || name;

  // Get features and benefits from ACF or parse from content
  const features = wpService.acf?.features || parseListItems(content).slice(0, 5);
  const benefits = wpService.acf?.benefits || parseListItems(content).slice(5, 9);

  // Get featured image URL
  const imageUrl = wpService._embedded?.['wp:featuredmedia']?.[0]?.source_url;

  // Extract category information
  let wordPressCategoryName: string | undefined;
  let mainCategoryId: string | undefined;
  let subCategoryId: string | undefined;

  // Try to get category from _embedded wp:term
  if (wpService._embedded?.['wp:term'] && Array.isArray(wpService._embedded['wp:term'])) {
    for (const termArray of wpService._embedded['wp:term']) {
      if (Array.isArray(termArray)) {
        for (const term of termArray) {
          if (term.taxonomy === 'service_category') {
            wordPressCategoryName = term.name;
            // Map to our category structure
            const mainCategory = getMainCategoryByWordPressCategory(term.name);
            const subCategory = getSubCategoryByWordPressCategory(term.name);
            if (mainCategory) {
              mainCategoryId = mainCategory.id;
            }
            if (subCategory) {
              subCategoryId = subCategory.id;
            }
            // Log for debugging
            if (!mainCategory || !subCategory) {
              console.warn(`⚠️ Category mapping not found for: "${term.name}" (slug: ${term.slug})`);
            }
            break;
          }
        }
      }
    }
  }

  // Create concise description (like Mobile Application Security Testing format)
  // Mobile App Security format: "ServiceName evaluates/assesses X for Y through Z methods"
  let description = excerpt;
  
  // Clean excerpt first
  if (description) {
    description = description.replace(/\[[^\]]+\]/g, '').replace(/<[^>]*>/g, '').trim();
  }
  
  // If excerpt is too short or generic, create from content
  if (!description || description.length < 20 || description.toLowerCase().includes('professional')) {
    // Extract key information from content
    const sentences = content.split(/[.!?]\s+/).filter(s => s.length > 20);
    
    // Look for a sentence that describes what the service does (like Mobile App Security format)
    const serviceSentence = sentences.find(s => {
      const lower = s.toLowerCase();
      // Look for action verbs that describe the service
      const hasActionVerb = lower.includes('evaluates') || lower.includes('assesses') || 
                           lower.includes('provides') || lower.includes('designs') ||
                           lower.includes('builds') || lower.includes('delivers') ||
                           lower.includes('develops') || lower.includes('implements');
      return hasActionVerb && s.length > 30 && s.length < 250;
    });
    
    if (serviceSentence) {
      description = serviceSentence.trim();
    } else if (sentences.length > 0) {
      // Use first substantial sentence
      const firstGood = sentences.find(s => s.length > 40 && s.length < 200);
      description = firstGood ? firstGood.trim() : sentences[0].trim();
    } else if (content.length > 20) {
      // Use first part of content
      description = content.substring(0, 150).trim();
    } else {
      // Fallback: create from service name (Mobile App Security style)
      description = `${name} provides professional solutions tailored to your organization's needs`;
    }
  }
  
  // Clean up description - remove any remaining shortcode artifacts
  description = description.replace(/\[[^\]]+\]/g, '').replace(/<[^>]*>/g, '').trim();
  
  // Ensure concise format (max 200 chars, like Mobile Application Security Testing)
  if (description.length > 200) {
    // Try to find a natural break point
    const breakPoint = description.lastIndexOf('.', 200);
    if (breakPoint > 100) {
      description = description.substring(0, breakPoint + 1);
    } else {
      // Find last space before 200 chars
      const spaceBreak = description.lastIndexOf(' ', 180);
      if (spaceBreak > 100) {
        description = description.substring(0, spaceBreak).trim() + '...';
      } else {
        description = description.substring(0, 180).trim() + '...';
      }
    }
  }
  
  // Ensure we have a valid description
  if (!description || description.length < 10) {
    description = `${name} provides professional solutions tailored to your needs`;
  }

  return {
    id: wpService.id,
    name,
    nameAr,
    description: description || 'Professional service solution',
    longDescription: content || excerpt || 'Comprehensive service offering',
    icon: getIconForService(name, slug),
    color: getColorForService(name, slug, wpService.acf?.color),
    features: features.length > 0 ? features : ['Expert Consultation', 'Custom Solutions', 'Proven Methodology'],
    benefits: benefits.length > 0 ? benefits : ['Improved Efficiency', 'Cost Savings', 'Enhanced Security'],
    slug,
    imageUrl,
    mainCategoryId,
    subCategoryId,
    wordPressCategoryName,
  };
}

/**
 * Map array of WordPress services to Service array
 */
export function mapWordPressServicesToServices(wpServices: WordPressService[]): Service[] {
  return wpServices.map(mapWordPressServiceToService);
}

